import React, { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { PaginationUI } from './PaginationUI';
import { ConditionalRendering } from '../conditional-rendering';

const MAX_NUM_PAGES = 3;
const ROWS_PER_PAGE = [10, 20, 30, 40, 50];

interface StyledContainerProps {
    children: ReactNode;
}

const StyledContainer = styled.div<StyledContainerProps>`
    display: grid;
    gap: 16px;
    max-width: 100%;
    position: relative;
    margin: auto;

    /* Mobile: stack items */
    grid-template-columns: 1fr;

    @media (min-width: 768px) {
        /* Desktop: three columns */
        grid-template-columns: repeat(3, 1fr);

        /* Align the three items */
        & > *:nth-child(1) {
            justify-self: start;
        }
        & > *:nth-child(2) {
            justify-self: center;
        }
        & > *:nth-child(3) {
            justify-self: end;
        }
    }
`;

const StyledDataRange = styled.p<{ children: ReactNode }>`
    color: #a0aec0;
    align-self: center;
    margin: 0;
`;

const StyledBox = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StyledSelect = styled.select<{ children: ReactNode; value: number; onChange: (e: any) => void }>`
    padding: 4px 8px;
`;

interface PaginationProps {
    pageIndex: number;
    pageCount: number;
    gotoPage: (page: number) => void;
    canPreviousPage: boolean;
    previousPage: () => void;
    nextPage: () => void;
    canNextPage: boolean;
    disablePagination?: boolean;
    rows: any[];
    setPageSize: (size: number) => void;
    isShowPaginationHeader?: boolean;
    isShowRowsPerPage?: boolean;
    pageSize: number;
    prevText?: string;
    nextText?: string;
    maxNumPages?: number;
    rowsPerPageOptions?: number[];
}

function Pagination({
    pageIndex,
    pageCount,
    gotoPage,
    canPreviousPage,
    previousPage,
    nextPage,
    canNextPage,
    disablePagination,
    rows,
    setPageSize,
    isShowPaginationHeader,
    isShowRowsPerPage,
    pageSize,
    prevText,
    nextText,
    maxNumPages = MAX_NUM_PAGES,
    rowsPerPageOptions = ROWS_PER_PAGE,
}: PaginationProps) {
    const onPageChange = (page: number) => {
        gotoPage(page - 1);
    };

    const pageObj = useMemo(() => {
        const retval: {
            start: number;
            end: number;
            pagesCount: number;
            newPagesCount: number;
            pages: number[];
        } = {
            start: pageIndex + 1,
            end: pageIndex + 1,
            pagesCount: 1,
            newPagesCount: 1,
            pages: [],
        };

        while (retval.pagesCount < maxNumPages) {
            if (retval.end + 1 <= pageCount) {
                retval.end++;
                retval.newPagesCount++;
            }
            if (retval.start - 1 > 0) {
                retval.start--;
                retval.newPagesCount++;
            }
            if (retval.newPagesCount === retval.pagesCount) {
                break;
            } else {
                retval.pagesCount = retval.newPagesCount;
            }
        }

        retval.pages = Array.from({ length: Math.min(maxNumPages, retval.end) }, (_, index) => retval.start + index);
        return retval;
    }, [pageIndex, rows, pageSize, maxNumPages, pageCount]);

    const startIndex = pageIndex * pageSize + 1;
    const endIndex = Math.min((pageIndex + 1) * pageSize, rows?.length) || null;

    return (
        <StyledContainer data-testid="pagination">
            <StyledDataRange data-testid="data-range">
                <ConditionalRendering isTrue={isShowPaginationHeader}>
                    {startIndex}-{endIndex} of {rows?.length}
                </ConditionalRendering>
            </StyledDataRange>

            <PaginationUI
                onNext={nextPage}
                prevText={prevText}
                nextText={nextText}
                pages={pageObj.pages}
                onPrev={previousPage}
                onPageChange={onPageChange}
                isNextDisabled={!canNextPage}
                isPrevDisabled={!canPreviousPage}
                disablePagination={disablePagination}
                isActive={(page: number) => pageIndex === page - 1}
            />
            <ConditionalRendering isTrue={isShowRowsPerPage}>
                <StyledBox>
                    <span>Rows</span>
                    <StyledSelect
                        data-testid="select-rows-per-page"
                        value={pageSize}
                        onChange={e => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {rowsPerPageOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </StyledSelect>
                </StyledBox>
            </ConditionalRendering>
        </StyledContainer>
    );
}

export default Pagination;
